import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Typography, Alert, Snackbar, Button } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { AppDispatch } from "../../store";
import { MainLayout } from "../../components/layout";
import { RightArrowIcon } from "../../components/Icons";
import { selectUser } from "../../store/selectors/authSelectors";
import { performMicrophoneCheck } from "../../utils/microphoneCheck";
import {
  useModal,
  StartSessionModalContent,
  SessionData,
  RecordingSessionModal,
} from "../../components/Modal";
import {
  selectDashboardProviders,
  selectSelectedProviderIds,
} from "../../store/selectors/dashboardSelectors";
import {
  setSelectedProviderIds,
  setProviders,
} from "../../store/slices/dashboardSlice";
import DashboardPatientsList from "../../components/DashboardPatientsList";
import FeedPanel, { FeedItemData } from "../../components/FeedPanel";
import ProvidersFilter from "../../components/ProvidersFilter";
import { dashboardService } from "../../services/dashboardService";
import { PROVIDER_ROLES, ASSISTANT_ROLES } from "../../constants";
import {
  DashboardContainer,
  DashboardHeader,
  DashboardTitle,
  StartSessionButton,
  DashboardContent,
  MainContent,
  RightPanel,
  HeaderControls,
  FeedPanelsContainer,
  DashboardMainContent,
} from "./Dashboard.styled";
import { usersService } from "../../services/usersService";

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(selectUser);
  const providers = useSelector(selectDashboardProviders);
  const selectedProviderIds = useSelector(selectSelectedProviderIds);
  const { openModal, closeModal } = useModal();

  // Notifications state
  const [notifications, setNotifications] = useState<FeedItemData[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [notificationsError, setNotificationsError] = useState<string | null>(
    null,
  );

  // Providers filter state
  const [providersLoading, setProvidersLoading] = useState(false);
  const providersLoadedRef = useRef(false);

  // Microphone check state
  const [micCheckError, setMicCheckError] = useState<{
    type:
      | "NO_DEVICE"
      | "PERMISSION_DENIED"
      | "SYSTEM_MUTED"
      | "BROWSER_NOT_SUPPORTED"
      | "UNKNOWN";
    message: string;
    title: string;
  } | null>(null);
  const [isCheckingMic, setIsCheckingMic] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    try {
      setNotificationsLoading(true);
      setNotificationsError(null);
      const response = await dashboardService.getNotifications();

      const feedItems: FeedItemData[] = response.map((notification) => ({
        id: notification.id,
        text: notification.text,
        time: notification.createdAt,
        type: notification.type,
        isRead: notification.isRead,
        patientName: notification.name,
      }));

      setNotifications(feedItems);
    } catch (err) {
      setNotificationsError("Failed to load notifications");
      console.error("Error fetching notifications:", err);
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  // Fetch providers on dropdown open (lazy loading)
  const handleProvidersFilterOpen = useCallback(async () => {
    // Only fetch once
    if (providersLoadedRef.current) return;

    try {
      setProvidersLoading(true);
      const providersData = await usersService.getProviders();
      dispatch(setProviders(providersData || []));
      providersLoadedRef.current = true;
    } catch (err) {
      console.error("Error fetching providers:", err);
    } finally {
      setProvidersLoading(false);
    }
  }, [dispatch]);

  // Fetch notifications on mount
  useEffect(() => {
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMarkAsRead = useCallback(async (notificationId: number) => {
    try {
      await dashboardService.markNotificationsAsRead([notificationId]);
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? { ...notification, isRead: true }
            : notification,
        ),
      );
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  }, []);

  // Show loading if user data is not available yet
  if (!user) {
    return (
      <MainLayout>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <Typography>{t("dashboard.loading")}</Typography>
        </Box>
      </MainLayout>
    );
  }

  const isProvider = PROVIDER_ROLES.includes(
    user.roleTitle as (typeof PROVIDER_ROLES)[number],
  );
  const isAssistant = ASSISTANT_ROLES.includes(
    user.roleTitle as (typeof ASSISTANT_ROLES)[number],
  );

  const getErrorTitle = (errorType: string): string => {
    switch (errorType) {
      case "NO_DEVICE":
        return "Microphone Not Found";
      case "PERMISSION_DENIED":
        return "Microphone Access Denied";
      case "SYSTEM_MUTED":
        return retryCount === 0
          ? "No Audio Input Detected"
          : "Microphone Not Found";
      case "BROWSER_NOT_SUPPORTED":
        return "Browser Not Supported";
      default:
        return "Microphone Error";
    }
  };

  const handleStartSession = async () => {
    setIsCheckingMic(true);
    setMicCheckError(null);

    try {
      const result = await performMicrophoneCheck();

      if (!result.success) {
        const errorType = result.errorType || "UNKNOWN";

        if (errorType === "SYSTEM_MUTED") {
          setRetryCount((prev) => prev + 1);
        }

        setMicCheckError({
          type: errorType,
          title: getErrorTitle(errorType),
          message:
            result.error ||
            "An unexpected error occurred while checking microphone. Please try again.",
        });
        return;
      }

      setRetryCount(0);

      openModal({
        id: "start-session-modal",
        title: t("startSessionModal.title"),
        content: <StartSessionModalContent onProceed={handleProceedSession} />,
        maxWidth: "sm",
        fullWidth: true,
        containerPadding: "40px",
        contentPadding: "0",
      });
    } catch (error) {
      console.error("Error during microphone check:", error);
      setMicCheckError({
        type: "UNKNOWN",
        title: "Microphone Error",
        message:
          "An unexpected error occurred while checking microphone. Please try again.",
      });
    } finally {
      setIsCheckingMic(false);
    }
  };

  const handleRetryMicrophone = () => {
    setMicCheckError(null);
    handleStartSession();
  };

  const handleCancelMicrophone = () => {
    setMicCheckError(null);
    setRetryCount(0);
  };

  const handleProceedSession = (sessionData: SessionData) => {
    console.log("Starting session with data:", sessionData);

    // Close the start session modal first
    closeModal("start-session-modal");

    // Use requestAnimationFrame to ensure the modal is closed before opening the new one
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        console.log("Opening recording session modal...");
        openModal({
          id: "recording-session-modal",
          title: "",
          content: (
            <RecordingSessionModal
              sessionId={sessionData.sessionId}
              sessionType={sessionData.sessionType}
              patientId={sessionData.patientId}
            />
          ),
          showCloseButton: false,
          closeOnBackdropClick: false,
          maxWidth: 660,
          width: "660px",
          fullWidth: false,
          containerPadding: "40px",
          contentPadding: "0",
        });
      });
    });
  };

  const handleProvidersChange = (providers: number[]) => {
    dispatch(setSelectedProviderIds(providers));
  };

  return (
    <MainLayout>
      <DashboardContainer>
        <DashboardHeader>
          <DashboardTitle>
            Hi, {user.displayName || user.firstName}!
          </DashboardTitle>
          <HeaderControls>
            {isAssistant && (
              <ProvidersFilter
                providers={providers}
                selectedProviders={selectedProviderIds}
                onProvidersChange={handleProvidersChange}
                onOpen={handleProvidersFilterOpen}
                loading={providersLoading}
              />
            )}
            {isProvider && (
              <StartSessionButton
                variant="contained"
                endIcon={<RightArrowIcon />}
                onClick={handleStartSession}
                disabled={isCheckingMic}
              >
                {isCheckingMic
                  ? t("dashboard.checkingMicrophone")
                  : t("dashboard.startSession")}
              </StartSessionButton>
            )}
          </HeaderControls>
        </DashboardHeader>

        <DashboardContent>
          <DashboardMainContent>
            <MainContent>
              <DashboardPatientsList />
            </MainContent>

            <RightPanel>
              <FeedPanelsContainer>
                <FeedPanel
                  title="Notifications and Alerts"
                  items={notifications}
                  loading={notificationsLoading}
                  error={notificationsError}
                  onRetry={fetchNotifications}
                  onItemAction={handleMarkAsRead}
                  actionButtonText="Mark as read"
                  emptyMessage="No notifications at this time"
                  className="feed-panel-no-background"
                />
              </FeedPanelsContainer>
            </RightPanel>
          </DashboardMainContent>
        </DashboardContent>

        {/* Microphone Error Snackbar */}
        <Snackbar
          open={!!micCheckError}
          autoHideDuration={null}
          onClose={handleCancelMicrophone}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity="error"
            sx={{
              width: "100%",
              minWidth: "400px",
              alignItems: "center",
            }}
            action={
              <Box sx={{ display: "flex", gap: 1, ml: 2 }}>
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleCancelMicrophone}
                  sx={{ fontWeight: 600 }}
                >
                  Cancel
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleRetryMicrophone}
                  sx={{
                    fontWeight: 600,
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                    },
                  }}
                >
                  Retry
                </Button>
              </Box>
            }
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "16px",
                  mb: 0.5,
                }}
              >
                {micCheckError?.title}
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                {micCheckError?.message}
              </Typography>
            </Box>
          </Alert>
        </Snackbar>
      </DashboardContainer>
    </MainLayout>
  );
};

export default Dashboard;
